import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Clock, Hammer, Shield, Crosshair, Package, Map, ChevronLeft, ChevronRight, Gem, FlaskConical, Layers, SlidersHorizontal, Info, HelpCircle, LogIn, RotateCcw, Plus, ChevronDown, Pickaxe, MapPin, Sparkles, Target, Leaf, FileText, AlertTriangle, Timer, Star } from 'lucide-react';
import { RifleIcon, PistolIcon, ShotgunIcon, SmgIcon, SniperIcon, AmmoIcon, HelmetIcon, ChestArmorIcon, ArmArmorIcon, LegArmorIcon, DismantleIcon, CraftGenericIcon } from '../components/CraftingIcons';
import styles from './CraftingBlueprints.module.css';
import sharedStyles from '../styles/shared.module.css';
import {
  craftingService,
  CraftingBlueprintListItem,
  CraftingBlueprintDetail,
  CraftingResource,
  BlueprintPool,
  CraftingStats,
  BlueprintsResponse,
  CraftingStatModifier,
  CraftingIngredient,
  SavedPlanEntry,
} from '../services/craftingService';
import { shipService } from '../services/shipService';
import { useAuth } from '../context/AuthContext';

type TabType = 'blueprints' | 'resources' | 'sources' | 'planner';

/** A single entry in the crafting planner */
interface PlannerEntry {
  blueprintId: number;
  name: string;
  category: string;
  subCategory: string | null;
  detail: CraftingBlueprintDetail;
  quantity: number;
  slotQualities: Record<string, number>; // keyed by slot name, 0-1000
}

/** Get unique slot names from a blueprint's stat modifiers */
function getSlotNames(bp: CraftingBlueprintDetail): string[] {
  const slots = new Set<string>();
  for (const mod of bp.statModifiers) slots.add(mod.slotName);
  return Array.from(slots);
}

/** Resolve slot name to material display name */
function slotMaterialLabel(bp: CraftingBlueprintDetail | null, slotName: string): string {
  if (!bp) return slotName;
  const ing = bp.ingredients.find(i => i.slotName === slotName);
  return ing ? (ing.resourceDisplayName || ing.resourceName) : slotName;
}

/** Build default slot qualities (all 500) from a blueprint */
function defaultSlotQualities(bp: CraftingBlueprintDetail): Record<string, number> {
  const q: Record<string, number> = {};
  for (const slot of getSlotNames(bp)) q[slot] = 500;
  return q;
}

/** Compute the combined (multiplicative) modifier for each stat across all slots */
function computeCombinedStats(bp: CraftingBlueprintDetail, slotQualities: Record<string, number>): { statName: string; combined: number; perSlot: { slotName: string; modifier: number }[] }[] {
  // Group modifiers by stat
  const statMap: Record<string, { slotName: string; mod: CraftingStatModifier }[]> = {};
  for (const mod of bp.statModifiers) {
    if (!statMap[mod.statName]) statMap[mod.statName] = [];
    statMap[mod.statName].push({ slotName: mod.slotName, mod });
  }
  return Object.entries(statMap).map(([statName, entries]) => {
    let combined = 1;
    const perSlot = entries.map(({ slotName, mod }) => {
      const q = slotQualities[slotName] ?? 500;
      const modifier = interpolateModifier(mod, q);
      combined *= modifier;
      return { slotName, modifier };
    });
    return { statName, combined, perSlot };
  });
}

function formatCraftTime(seconds: number): string {
  if (seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}

function getCategoryStyle(category: string, subCategory: string | null) {
  if (category === 'FPSWeapons') return styles.categoryWeapon;
  if (category === 'FPSArmours') return styles.categoryArmor;
  if (category === 'Dismantle') return styles.categoryDismantle;
  return styles.categoryArmor;
}

/** Get an icon + color scheme for a blueprint based on its category/subcategory */
function getCategoryIcon(category: string, subCategory: string | null): { icon: React.ReactNode; gradient: string } {
  const sub = (subCategory || '').toLowerCase();
  if (category === 'FPSWeapons') {
    if (sub.includes('sniper')) return { icon: <SniperIcon size={28} />, gradient: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' };
    if (sub.includes('shotgun')) return { icon: <ShotgunIcon size={28} />, gradient: 'linear-gradient(135deg, #ea580c 0%, #9a3412 100%)' };
    if (sub.includes('smg') || sub.includes('lmg')) return { icon: <SmgIcon size={28} />, gradient: 'linear-gradient(135deg, #e11d48 0%, #881337 100%)' };
    if (sub.includes('pistol')) return { icon: <PistolIcon size={28} />, gradient: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)' };
    if (sub.includes('ammo')) return { icon: <AmmoIcon size={28} />, gradient: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)' };
    return { icon: <RifleIcon size={28} />, gradient: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)' };
  }
  if (category === 'FPSArmours') {
    if (sub.includes('helmet') || sub.includes('head')) return { icon: <HelmetIcon size={28} />, gradient: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' };
    if (sub.includes('chest') || sub.includes('torso') || sub.includes('core')) return { icon: <ChestArmorIcon size={28} />, gradient: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)' };
    if (sub.includes('arm')) return { icon: <ArmArmorIcon size={28} />, gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)' };
    if (sub.includes('leg')) return { icon: <LegArmorIcon size={28} />, gradient: 'linear-gradient(135deg, #06b6d4 0%, #0e7490 100%)' };
    return { icon: <ChestArmorIcon size={28} />, gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' };
  }
  if (category === 'Dismantle') return { icon: <DismantleIcon size={28} />, gradient: 'linear-gradient(135deg, #a855f7 0%, #6b21a8 100%)' };
  return { icon: <CraftGenericIcon size={28} />, gradient: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)' };
}

function getCategoryLabel(category: string, subCategory: string | null): string {
  if (category === 'FPSWeapons') {
    if (subCategory) return subCategory.split('/').pop() || 'Weapon';
    return 'Weapon';
  }
  if (category === 'FPSArmours') {
    if (subCategory) {
      const parts = subCategory.split('/');
      return parts.length > 1 ? `${parts[parts.length - 1]} Armor` : `${parts[0]} Armor`;
    }
    return 'Armor';
  }
  if (category === 'Dismantle') return 'Dismantle';
  return category;
}

// Stats where LOWER multiplier = BETTER (recoil, spread, kick)
const INVERTED_STATS = new Set([
  'Recoil Smoothness', 'Recoil Handling', 'Recoil Kick', 'Spread',
  'RecoilSmoothness', 'RecoilHandling', 'RecoilKick',
]);

function isInvertedStat(statName: string): boolean {
  return INVERTED_STATS.has(statName) || 
    statName.toLowerCase().includes('recoil') || 
    statName.toLowerCase().includes('spread') ||
    statName.toLowerCase().includes('kick');
}

/** Compute the modifier value at a given quality (linear interpolation, clamped).
 *  Some modifiers start at Q500, meaning below that quality the modifier stays at modifierAtStart. */
function interpolateModifier(mod: CraftingStatModifier, quality: number): number {
  if (quality <= mod.startQuality) return mod.modifierAtStart;
  if (quality >= mod.endQuality) return mod.modifierAtEnd;
  const t = (quality - mod.startQuality) / (mod.endQuality - mod.startQuality);
  return mod.modifierAtStart + t * (mod.modifierAtEnd - mod.modifierAtStart);
}

/** Format a modifier as a percentage change */
function formatModifier(value: number): string {
  const pct = ((value - 1) * 100);
  if (pct >= 0) return `+${pct.toFixed(1)}%`;
  return `${pct.toFixed(1)}%`;
}

/** Determine if a modifier value is "good" considering stat type */
function isGoodModifier(value: number, statName: string): boolean {
  const inverted = isInvertedStat(statName);
  if (inverted) return value < 1; // Lower = better for recoil/spread
  return value > 1; // Higher = better for damage/fire rate
}

/** Clean up stat name for display */
function cleanStatName(raw: string): string {
  return raw
    .replace(/^GPP_/, '')
    .replace(/^Weapon_/, '')
    .replace(/^Armor_/, '')
    .replace(/^Crafter_/, '')
    .replace(/_/g, ' ');
}

/** Explain what a stat does */
function getStatTooltip(statName: string): string {
  const lower = statName.toLowerCase();
  if (lower.includes('recoil') && lower.includes('smooth')) return 'How quickly recoil settles. Lower = less recoil.';
  if (lower.includes('recoil') && lower.includes('handling')) return 'How controllable recoil is. Lower = easier to control.';
  if (lower.includes('recoil') && lower.includes('kick')) return 'Vertical kick per shot. Lower = less muzzle climb.';
  if (lower.includes('spread')) return 'Bullet spread/cone of fire. Lower = tighter grouping.';
  if (lower.includes('damage')) return 'Damage per hit. Higher = more damage dealt.';
  if (lower.includes('firerate') || lower.includes('fire rate')) return 'Rate of fire. Higher = faster shooting.';
  if (lower.includes('reload')) return 'Reload speed. Higher = faster reloads.';
  if (lower.includes('damagemitigation')) return 'Damage reduction from armor. Higher = better protection.';
  if (lower.includes('radiationcapacity')) return 'How much radiation the armor can absorb. Higher = more protection.';
  if (lower.includes('radiationdissipation')) return 'How fast radiation drains. Higher = faster recovery.';
  if (lower.includes('temperaturemax')) return 'Max temperature tolerance. Higher = more heat resistance.';
  if (lower.includes('temperaturemin')) return 'Min temperature tolerance. Higher = better cold resistance.';
  if (lower.includes('craftspeed')) return 'Crafting speed bonus. Higher = faster crafting.';
  if (lower.includes('dismantleefficiency')) return 'Material recovery when dismantling. Higher = more materials returned.';
  return 'Affects crafted item performance based on material quality.';
}

const CraftingBlueprints: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('blueprints');
  const [stats, setStats] = useState<CraftingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versionInfo, setVersionInfo] = useState<{ scVersion: string; dataSource: string }>({ scVersion: 'Alpha', dataSource: 'LIVE' });

  // Blueprints tab state
  const [blueprints, setBlueprintsData] = useState<BlueprintsResponse | null>(null);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [subCategories, setSubCategories] = useState<string[]>([]);

  // Detail modal
  const [selectedBlueprint, setSelectedBlueprint] = useState<CraftingBlueprintDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [slotQualities, setSlotQualities] = useState<Record<string, number>>({}); // per-slot quality, keyed by slot name

  // Resources tab state
  const [resources, setResources] = useState<CraftingResource[]>([]);

  // Sources tab state
  const [pools, setPools] = useState<BlueprintPool[]>([]);
  const [factionFilter, setFactionFilter] = useState('');

  // Planner state
  const [plannerEntries, setPlannerEntries] = useState<PlannerEntry[]>([]);
  const [plannerSearch, setPlannerSearch] = useState('');
  const [plannerSearchInput, setPlannerSearchInput] = useState('');
  const [plannerSearchResults, setPlannerSearchResults] = useState<CraftingBlueprintListItem[]>([]);
  const [plannerSearchLoading, setPlannerSearchLoading] = useState(false);
  const [plannerLoading, setPlannerLoading] = useState(false);
  const [plannerSaving, setPlannerSaving] = useState(false);
  const plannerLoaded = useRef(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load saved planner from API when user is authenticated
  useEffect(() => {
    if (!token || plannerLoaded.current) return;
    let cancelled = false;
    (async () => {
      setPlannerLoading(true);
      try {
        const saved = await craftingService.getPlan();
        if (cancelled || saved.length === 0) { plannerLoaded.current = true; setPlannerLoading(false); return; }
        // Hydrate full details for each saved entry
        const entries: PlannerEntry[] = [];
        for (const s of saved) {
          try {
            const detail = await craftingService.getBlueprintById(s.blueprintId);
            const quals: Record<string, number> = (() => { try { return JSON.parse(s.slotQualities || '{}'); } catch { return {}; } })();
            entries.push({
              blueprintId: s.blueprintId,
              name: detail.outputItemName || detail.displayName || detail.recordName,
              category: detail.category,
              subCategory: detail.subCategory,
              detail,
              quantity: s.quantity,
              slotQualities: { ...defaultSlotQualities(detail), ...quals },
            });
          } catch { /* skip blueprints that fail to load */ }
        }
        if (!cancelled) setPlannerEntries(entries);
        plannerLoaded.current = true;
      } catch (err) {
        console.error('Failed to load crafting plan:', err);
      } finally {
        if (!cancelled) setPlannerLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Auto-save planner to API when entries change (debounced)
  useEffect(() => {
    if (!token || !plannerLoaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setPlannerSaving(true);
      try {
        const toSave: SavedPlanEntry[] = plannerEntries.map(e => ({
          blueprintId: e.blueprintId,
          quantity: e.quantity,
          slotQualities: JSON.stringify(e.slotQualities),
        }));
        await craftingService.savePlan(toSave);
      } catch (err) {
        console.error('Failed to save crafting plan:', err);
      } finally {
        setPlannerSaving(false);
      }
    }, 1000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [plannerEntries, token]);

  // Load stats + version on mount
  useEffect(() => {
    craftingService.getStats()
      .then(setStats)
      .catch(err => console.error('Failed to load stats:', err));
    shipService.getVersion()
      .then(setVersionInfo)
      .catch(err => console.error('Failed to fetch version:', err));
  }, []);

  // Load blueprints
  const loadBlueprints = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await craftingService.getBlueprints({
        page,
        pageSize: 30,
        category: category || undefined,
        subCategory: subCategory || undefined,
        search: search || undefined,
        sortBy,
      });
      setBlueprintsData(data);
    } catch (err) {
      setError('Failed to load blueprints');
    } finally {
      setLoading(false);
    }
  }, [page, category, subCategory, search, sortBy]);

  // Load resources
  const loadResources = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await craftingService.getResources();
      setResources(data);
    } catch (err) {
      setError('Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load pools
  const loadPools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await craftingService.getPools(factionFilter || undefined);
      setPools(data);
    } catch (err) {
      setError('Failed to load blueprint sources');
    } finally {
      setLoading(false);
    }
  }, [factionFilter]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 'blueprints') loadBlueprints();
    else if (activeTab === 'resources') loadResources();
    else if (activeTab === 'sources') loadPools();
  }, [activeTab, loadBlueprints, loadResources, loadPools]);

  // Load subcategories when category changes
  useEffect(() => {
    if (category) {
      craftingService.getSubCategories(category).then(setSubCategories);
    } else {
      setSubCategories([]);
    }
    setSubCategory('');
    setPage(1);
  }, [category]);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Open blueprint detail
  const openDetail = async (id: number) => {
    setDetailLoading(true);
    try {
      const detail = await craftingService.getBlueprintById(id);
      setSelectedBlueprint(detail);
      setSlotQualities(defaultSlotQualities(detail));
    } catch (err) {
      console.error('Failed to load blueprint detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  // ---- Planner functions ----

  // Search debounce for planner
  useEffect(() => {
    if (!plannerSearchInput.trim()) {
      setPlannerSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setPlannerSearchLoading(true);
      try {
        const data = await craftingService.getBlueprints({
          page: 1,
          pageSize: 10,
          search: plannerSearchInput,
          sortBy: 'name',
        });
        setPlannerSearchResults(data.items);
      } catch {
        setPlannerSearchResults([]);
      } finally {
        setPlannerSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [plannerSearchInput]);

  const addToPlanner = async (bp: CraftingBlueprintListItem) => {
    // Don't add duplicates
    if (plannerEntries.some(e => e.blueprintId === bp.id)) return;
    try {
      const detail = await craftingService.getBlueprintById(bp.id);
      setPlannerEntries(prev => [...prev, {
        blueprintId: bp.id,
        name: bp.outputItemName || bp.displayName || bp.recordName,
        category: bp.category,
        subCategory: bp.subCategory,
        detail,
        quantity: 1,
        slotQualities: defaultSlotQualities(detail),
      }]);
      setPlannerSearchInput('');
      setPlannerSearchResults([]);
    } catch (err) {
      console.error('Failed to load blueprint for planner:', err);
    }
  };

  const addToPlannerFromDetail = (bp: CraftingBlueprintDetail) => {
    if (plannerEntries.some(e => e.blueprintId === bp.id)) return;
    setPlannerEntries(prev => [...prev, {
      blueprintId: bp.id,
      name: bp.outputItemName || bp.displayName || bp.recordName,
      category: bp.category,
      subCategory: bp.subCategory,
      detail: bp,
      quantity: 1,
      slotQualities: defaultSlotQualities(bp),
    }]);
  };

  const removeFromPlanner = (blueprintId: number) => {
    setPlannerEntries(prev => prev.filter(e => e.blueprintId !== blueprintId));
  };

  const updatePlannerEntry = (blueprintId: number, updates: Partial<Pick<PlannerEntry, 'quantity' | 'slotQualities'>>) => {
    setPlannerEntries(prev => prev.map(e =>
      e.blueprintId === blueprintId ? { ...e, ...updates } : e
    ));
  };

  const updatePlannerSlotQuality = (blueprintId: number, slotName: string, quality: number) => {
    setPlannerEntries(prev => prev.map(e =>
      e.blueprintId === blueprintId
        ? { ...e, slotQualities: { ...e.slotQualities, [slotName]: quality } }
        : e
    ));
  };

  const setPlannerAllSlots = (blueprintId: number, quality: number) => {
    setPlannerEntries(prev => prev.map(e => {
      if (e.blueprintId !== blueprintId) return e;
      const sq: Record<string, number> = {};
      for (const slot of getSlotNames(e.detail)) sq[slot] = quality;
      return { ...e, slotQualities: sq };
    }));
  };

  // Compute aggregated material requirements
  const plannerMaterialSummary = React.useMemo(() => {
    const materialMap: Record<string, { displayName: string; totalScu: number; blueprintCount: number; miningType: string | null; rarity: string | null; location: string | null; targetQuality: number }> = {};
    for (const entry of plannerEntries) {
      for (const ing of entry.detail.ingredients) {
        const key = ing.resourceName;
        if (!materialMap[key]) {
          materialMap[key] = { displayName: ing.resourceDisplayName || ing.resourceName, totalScu: 0, blueprintCount: 0, miningType: ing.miningType || null, rarity: ing.rarity || null, location: ing.location || null, targetQuality: 0 };
        }
        materialMap[key].totalScu += ing.quantityScu * entry.quantity;
        materialMap[key].blueprintCount += 1;
        // Track the highest quality the user needs for this material across all entries
        const slotQ = entry.slotQualities[ing.slotName] ?? 500;
        if (slotQ > materialMap[key].targetQuality) {
          materialMap[key].targetQuality = slotQ;
        }
      }
    }
    return Object.entries(materialMap)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.totalScu - a.totalScu);
  }, [plannerEntries]);

  // Compute total craft time
  const plannerTotalCraftTime = React.useMemo(() => {
    return plannerEntries.reduce((sum, e) => sum + (e.detail.craftTimeSeconds * e.quantity), 0);
  }, [plannerEntries]);

  // Compute quality effects summary for planner (per-entry combined stats)
  const plannerQualityEffects = React.useMemo(() => {
    return plannerEntries.map(entry => ({
      entryName: entry.name,
      stats: computeCombinedStats(entry.detail, entry.slotQualities),
    }));
  }, [plannerEntries]);

  // Render pagination
  const renderPagination = () => {
    if (!blueprints || blueprints.totalPages <= 1) return null;
    const pages = [];
    const total = blueprints.totalPages;
    const current = blueprints.page;

    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return (
      <div className={styles.pagination}>
        <button
          className={styles.pageButton}
          disabled={current <= 1}
          onClick={() => setPage(current - 1)}
        >
          <ChevronLeft size={16} />
        </button>
        {pages.map(p => (
          <button
            key={p}
            className={`${styles.pageNumber} ${p === current ? styles.pageNumberActive : ''}`}
            onClick={() => setPage(p)}
          >
            {p}
          </button>
        ))}
        <button
          className={styles.pageButton}
          disabled={current >= total}
          onClick={() => setPage(current + 1)}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Crafting Blueprints</h1>
          <span className={`${sharedStyles.dataSourceBadge} ${sharedStyles.dataSourceLive}`}>
            <span className={sharedStyles.dataSourceDot}></span>
            {versionInfo.scVersion}
          </span>
        </div>
        <p className={styles.subtitle}>
          Explore crafting recipes, material requirements, and discover how to obtain blueprints from mission rewards
        </p>
        <p className={styles.subtitle} style={{ fontSize: '0.85rem', opacity: 0.6, marginTop: '-0.75rem' }}>
          Blueprint images sourced from the <a href="https://starcitizen.tools" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-secondary)' }}>Star Citizen Wiki</a> community. Thank you to all contributors!
        </p>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className={styles.statsBar}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.totalBlueprints}</span>
            <span className={styles.statLabel}>Blueprints</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.weaponBlueprints}</span>
            <span className={styles.statLabel}>Weapons</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.armorBlueprints}</span>
            <span className={styles.statLabel}>Armor</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.totalResources}</span>
            <span className={styles.statLabel}>Materials</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{stats.totalPools}</span>
            <span className={styles.statLabel}>Reward Pools</span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tab} ${activeTab === 'blueprints' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('blueprints')}
        >
          <Hammer size={18} />
          Blueprints
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'resources' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('resources')}
        >
          <Gem size={18} />
          Materials
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'sources' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('sources')}
        >
          <Map size={18} />
          How to Obtain
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'planner' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('planner')}
        >
          <SlidersHorizontal size={18} />
          Crafting Planner
          {plannerEntries.length > 0 && (
            <span className={styles.plannerBadge}>{plannerEntries.length}</span>
          )}
        </button>
      </div>

      {/* ===== BLUEPRINTS TAB ===== */}
      {activeTab === 'blueprints' && (
        <>
          {/* Filters */}
          <div className={styles.filtersContainer}>
            <div className={styles.filtersRow}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>Search</label>
                <div style={{ position: 'relative' }}>
                  <input
                    className={styles.searchInput}
                    type="text"
                    placeholder="Search blueprints..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <X
                      size={16}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: 'var(--text-muted)' }}
                      onClick={() => { setSearchInput(''); setSearch(''); }}
                    />
                  )}
                </div>
              </div>
              <div className={styles.filterGroup} style={{ flex: '0 0 160px' }}>
                <label className={styles.filterLabel}>Category</label>
                <select
                  className={styles.filterSelect}
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  <option value="FPSWeapons">Weapons</option>
                  <option value="FPSArmours">Armor</option>
                </select>
              </div>
              {subCategories.length > 0 && (
                <div className={styles.filterGroup} style={{ flex: '0 0 160px' }}>
                  <label className={styles.filterLabel}>Sub-Category</label>
                  <select
                    className={styles.filterSelect}
                    value={subCategory}
                    onChange={e => { setSubCategory(e.target.value); setPage(1); }}
                  >
                    <option value="">All</option>
                    {subCategories.map(sc => (
                      <option key={sc} value={sc}>{sc}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className={styles.filterGroup} style={{ flex: '0 0 140px' }}>
                <label className={styles.filterLabel}>Sort By</label>
                <select
                  className={styles.filterSelect}
                  value={sortBy}
                  onChange={e => { setSortBy(e.target.value); setPage(1); }}
                >
                  <option value="name">Name</option>
                  <option value="category">Category</option>
                  <option value="crafttime">Craft Time</option>
                  <option value="subcategory">Sub-Category</option>
                </select>
              </div>
              {(search || category || subCategory) && (
                <button
                  className={styles.clearButton}
                  onClick={() => {
                    setSearchInput('');
                    setSearch('');
                    setCategory('');
                    setSubCategory('');
                    setPage(1);
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Results info */}
          {blueprints && (
            <div className={styles.viewControls}>
              <span className={styles.resultsInfo}>
                Showing {((blueprints.page - 1) * blueprints.pageSize) + 1}–{Math.min(blueprints.page * blueprints.pageSize, blueprints.totalCount)} of {blueprints.totalCount} blueprints
              </span>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <span>Loading blueprints...</span>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className={styles.error}>
              <span>{error}</span>
              <button className={styles.retryButton} onClick={loadBlueprints}>Retry</button>
            </div>
          )}

          {/* Blueprint Grid */}
          {!loading && !error && blueprints && (
            <>
              {/* Icon Legend */}
              <details className={styles.legendDetails}>
                <summary className={styles.legendSummary}>
                  <HelpCircle size={14} /> Icon Legend <ChevronDown size={14} className={styles.legendChevron} />
                </summary>
                <div className={styles.legendGrid}>
                  <div className={styles.legendSection}>
                    <span className={styles.legendSectionTitle}>Weapons</span>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)' }}><RifleIcon size={16} /></div> Assault Rifle / LMG</div>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)' }}><PistolIcon size={16} /></div> Pistol</div>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #ea580c 0%, #9a3412 100%)' }}><ShotgunIcon size={16} /></div> Shotgun</div>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #e11d48 0%, #881337 100%)' }}><SmgIcon size={16} /></div> SMG</div>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)' }}><SniperIcon size={16} /></div> Sniper Rifle</div>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)' }}><AmmoIcon size={16} /></div> Ammunition</div>
                  </div>
                  <div className={styles.legendSection}>
                    <span className={styles.legendSectionTitle}>Armor</span>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' }}><HelmetIcon size={16} /></div> Helmet</div>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)' }}><ChestArmorIcon size={16} /></div> Chest / Core</div>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)' }}><ArmArmorIcon size={16} /></div> Arms</div>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0e7490 100%)' }}><LegArmorIcon size={16} /></div> Legs</div>
                  </div>
                  <div className={styles.legendSection}>
                    <span className={styles.legendSectionTitle}>Other</span>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #a855f7 0%, #6b21a8 100%)' }}><DismantleIcon size={16} /></div> Dismantle</div>
                    <div className={styles.legendItem}><div className={styles.legendSwatch} style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)' }}><CraftGenericIcon size={16} /></div> General / Other</div>
                  </div>
                </div>
              </details>

              {blueprints.items.length === 0 ? (
                <div className={styles.empty}>
                  <Search size={48} />
                  <span>No blueprints found matching your filters</span>
                </div>
              ) : (
                <div className={styles.grid}>
                  {blueprints.items.map(bp => {
                    const { icon, gradient } = getCategoryIcon(bp.category, bp.subCategory);
                    return (
                    <div key={bp.id} className={styles.card} onClick={() => openDetail(bp.id)}>
                      {bp.imageUrl ? (
                        <div className={styles.cardImageBanner}>
                          <img src={bp.imageUrl} alt="" className={styles.cardBannerImg} title="Image may not be exact to variant" />
                        </div>
                      ) : (
                        <div className={styles.cardIconBanner} style={{ background: gradient }}>
                          {icon}
                        </div>
                      )}
                      <div className={styles.cardBody}>
                        <div className={styles.cardHeaderStacked}>
                          <span className={styles.cardName}>
                            {bp.outputItemName || bp.displayName || bp.recordName}
                          </span>
                          <span className={`${styles.cardCategory} ${getCategoryStyle(bp.category, bp.subCategory)}`}>
                            {getCategoryLabel(bp.category, bp.subCategory)}
                          </span>
                        </div>
                        <div className={styles.cardMeta}>
                          <div className={styles.metaRow}>
                            <Clock size={16} />
                            <span>Craft time: {formatCraftTime(bp.craftTimeSeconds)}</span>
                          </div>
                          <div className={styles.metaRow}>
                            <Package size={16} />
                            <span>{bp.ingredientCount} material{bp.ingredientCount !== 1 ? 's' : ''} required</span>
                          </div>
                        </div>
                        <button
                          className={`${styles.addToPlannerBtn} ${plannerEntries.some(e => e.blueprintId === bp.id) ? styles.addToPlannerBtnAdded : ''}`}
                          onClick={(e) => { e.stopPropagation(); addToPlanner(bp); }}
                          disabled={plannerEntries.some(e => e.blueprintId === bp.id)}
                          title={plannerEntries.some(e => e.blueprintId === bp.id) ? 'Already in planner' : 'Add to planner'}
                        >
                          <Plus size={14} />
                          {plannerEntries.some(e => e.blueprintId === bp.id) ? 'In Planner' : 'Add to Planner'}
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
              {renderPagination()}
            </>
          )}
        </>
      )}

      {/* ===== RESOURCES TAB ===== */}
      {activeTab === 'resources' && (
        <>
          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <span>Loading materials...</span>
            </div>
          )}
          {error && !loading && (
            <div className={styles.error}>
              <span>{error}</span>
              <button className={styles.retryButton} onClick={loadResources}>Retry</button>
            </div>
          )}
          {!loading && !error && (
            <div className={styles.resourceGrid}>
              {resources.map(res => (
                <div key={res.id} className={styles.resourceCard}>
                  <div className={styles.resourceName}>{res.displayName}</div>
                  <div className={styles.resourceCount}>
                    Used in {res.usedInBlueprintCount} blueprint{res.usedInBlueprintCount !== 1 ? 's' : ''}
                  </div>
                  {res.refinedFromName && (
                    <div className={styles.resourceBadge}>
                      Refined from {res.refinedFromName}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ===== SOURCES TAB ===== */}
      {activeTab === 'sources' && (
        <>
          {/* Faction filter */}
          {stats && (
            <div className={styles.filtersContainer}>
              <div className={styles.filtersRow}>
                <div className={styles.filterGroup} style={{ flex: '0 0 200px' }}>
                  <label className={styles.filterLabel}>Faction</label>
                  <select
                    className={styles.filterSelect}
                    value={factionFilter}
                    onChange={e => setFactionFilter(e.target.value)}
                  >
                    <option value="">All Factions</option>
                    {stats.factions.map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <span>Loading blueprint sources...</span>
            </div>
          )}
          {error && !loading && (
            <div className={styles.error}>
              <span>{error}</span>
              <button className={styles.retryButton} onClick={loadPools}>Retry</button>
            </div>
          )}
          {!loading && !error && (
            <div className={styles.poolGrid}>
              {pools.map(pool => {
                // Use contractors if available, fall back to pool faction/missionType
                const displayFaction = pool.contractors && pool.contractors.length > 0
                  ? Array.from(new Set(pool.contractors.map(c => c.contractorName))).join(' / ')
                  : pool.faction;
                const displayMission = pool.contractors && pool.contractors.length > 0
                  ? Array.from(new Set(pool.contractors.map(c => c.missionName))).join(', ')
                  : pool.missionType;
                return (
                <div key={pool.id} className={styles.poolCard}>
                  <div className={styles.poolHeader}>
                    <div className={styles.poolFaction}>{displayFaction}</div>
                    <div className={styles.poolMission}>{displayMission}</div>
                  </div>
                  <div className={styles.poolBody}>
                    <div className={styles.poolEntryCount}>
                      {pool.entries.length} blueprint{pool.entries.length !== 1 ? 's' : ''} available
                    </div>
                    <div className={styles.poolEntryList}>
                      {pool.entries.map((entry, idx) => (
                        <div
                          key={idx}
                          className={styles.poolEntry}
                          style={{ cursor: entry.blueprintId ? 'pointer' : 'default' }}
                          onClick={() => entry.blueprintId && openDetail(entry.blueprintId)}
                        >
                          {entry.imageUrl && (
                            <img src={entry.imageUrl} alt="" className={styles.poolEntryImage} />
                          )}
                          <span>{entry.outputItemName || entry.blueprintDisplayName || entry.blueprintName || 'Unknown'}</span>
                          {entry.category && (
                            <span className={`${styles.cardCategory} ${getCategoryStyle(entry.category, null)}`}>
                              {entry.category === 'FPSWeapons' ? 'Weapon' : 'Armor'}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ); })}
            </div>
          )}
        </>
      )}

      {/* ===== PLANNER TAB ===== */}
      {activeTab === 'planner' && (
        <>
          {!token ? (
            <div className={styles.plannerAuthGate}>
              <LogIn size={48} />
              <h3>Login Required</h3>
              <p>Sign in to save your crafting plan. Your plan will persist across sessions and devices.</p>
              <a href="/login" className={styles.plannerLoginBtn}>Sign In</a>
            </div>
          ) : plannerLoading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <span>Loading your crafting plan...</span>
            </div>
          ) : (
          <>
          {/* Saving indicator */}
          {plannerSaving && <div className={styles.plannerSavingIndicator}>Saving...</div>}
          {/* Search to add blueprints */}
          <div className={styles.plannerAddSection}>
            <h3 className={styles.plannerAddTitle}>
              <Search size={16} />
              Add Blueprints to Plan
            </h3>
            <div className={styles.plannerSearchWrapper}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search blueprints by name..."
                value={plannerSearchInput}
                onChange={e => setPlannerSearchInput(e.target.value)}
              />
              {plannerSearchLoading && <div className={styles.plannerSearchSpinner}></div>}
            </div>
            {plannerSearchResults.length > 0 && (
              <div className={styles.plannerSearchResults}>
                {plannerSearchResults.map(bp => {
                  const alreadyAdded = plannerEntries.some(e => e.blueprintId === bp.id);
                  return (
                    <div
                      key={bp.id}
                      className={`${styles.plannerSearchItem} ${alreadyAdded ? styles.plannerSearchItemDisabled : ''}`}
                      onClick={() => !alreadyAdded && addToPlanner(bp)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {bp.imageUrl && (
                          <img src={bp.imageUrl} alt="" className={styles.plannerSearchImage} />
                        )}
                        <span className={styles.plannerSearchName}>
                          {bp.outputItemName || bp.displayName || bp.recordName}
                        </span>
                        <span className={`${styles.cardCategory} ${getCategoryStyle(bp.category, bp.subCategory)}`} style={{ marginLeft: 8, fontSize: '0.6rem' }}>
                          {getCategoryLabel(bp.category, bp.subCategory)}
                        </span>
                      </div>
                      <span className={styles.plannerSearchAction}>
                        {alreadyAdded ? 'Added' : '+ Add'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {plannerEntries.length === 0 ? (
            <div className={styles.empty}>
              <SlidersHorizontal size={48} />
              <span>Your crafting plan is empty</span>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Search and add blueprints above to start planning your crafting session
              </span>
            </div>
          ) : (
            <>
              {/* Planner entries list */}
              <div className={styles.plannerList}>
                {plannerEntries.map(entry => (
                  <div key={entry.blueprintId} className={styles.plannerEntry}>
                    {/* ─── Top: Item Identity ─── */}
                    <div className={styles.plannerEntryHeader}>
                      <div className={styles.plannerEntryIdentity}>
                        {entry.detail.imageUrl && (
                          <img src={entry.detail.imageUrl} alt="" className={styles.plannerItemImage} />
                        )}
                        <div className={styles.plannerEntryTitleBlock}>
                          <div className={styles.plannerEntryNameRow}>
                            <span className={styles.plannerEntryName}>{entry.name}</span>
                            <span className={`${styles.cardCategory} ${getCategoryStyle(entry.category, entry.subCategory)}`}>
                              {getCategoryLabel(entry.category, entry.subCategory)}
                            </span>
                          </div>
                          {/* Craft time + quantity inline */}
                          <div className={styles.plannerEntrySubRow}>
                            <span className={styles.plannerSubDetail}>
                              <Clock size={12} /> {formatCraftTime(entry.detail.craftTimeSeconds * entry.quantity)}
                            </span>
                            <span className={styles.plannerSubDetail}>
                              <Layers size={12} /> {entry.detail.ingredients.length} material{entry.detail.ingredients.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        className={styles.plannerRemoveButton}
                        onClick={() => removeFromPlanner(entry.blueprintId)}
                        title="Remove from plan"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {/* ─── Mission Source ─── */}
                    <div className={styles.plannerSection}>
                      <div className={styles.plannerSectionHeader}>
                        <Shield size={13} />
                        <span>How to Obtain Blueprint</span>
                      </div>
                      <div className={styles.plannerSectionBody}>
                        {entry.detail.availableFrom.length > 0 ? (
                          <div className={styles.plannerObtainGrid}>
                            {(() => {
                              const sources: { name: string; mission: string; title?: string; description?: string; difficulty?: string; timeMinutes?: number; rep?: string; systems?: string }[] = [];
                              const seen = new Set<string>();
                              entry.detail.availableFrom.forEach(src => {
                                if (src.contractors && src.contractors.length > 0) {
                                  src.contractors.forEach(c => {
                                    const key = `${c.contractorName}|${c.missionName}`;
                                    if (!seen.has(key)) {
                                      seen.add(key);
                                      sources.push({
                                        name: c.contractorName,
                                        mission: c.missionName,
                                        title: c.missionTitle,
                                        description: c.missionDescription,
                                        difficulty: c.missionDifficulty,
                                        timeMinutes: c.missionTimeMinutes,
                                        rep: c.requiresReputation,
                                        systems: c.systems,
                                      });
                                    }
                                  });
                                } else {
                                  const key = `${src.faction}|${src.missionType}`;
                                  if (!seen.has(key)) { seen.add(key); sources.push({ name: src.faction, mission: src.missionType }); }
                                }
                              });
                              return sources.map((s, idx) => (
                                <div key={idx} className={styles.plannerMissionCard}>
                                  <div className={styles.missionCardHeader}>
                                    <div className={styles.missionCardIcon}>
                                      <Target size={18} />
                                    </div>
                                    <div className={styles.missionCardHeaderInfo}>
                                      <span className={styles.missionCardContractor}>{s.name}</span>
                                      <span className={styles.missionCardType}>{s.mission}</span>
                                    </div>
                                  </div>
                                  {s.title && (
                                    <div className={styles.missionCardTitle}>
                                      <FileText size={13} />
                                      <span>{s.title}</span>
                                    </div>
                                  )}
                                  {s.description && (
                                    <div className={styles.missionCardBriefing}>
                                      <p>{s.description}</p>
                                    </div>
                                  )}
                                  <div className={styles.missionCardMeta}>
                                    {s.timeMinutes && s.timeMinutes > 0 && (
                                      <span className={styles.missionMetaTag}>
                                        <Timer size={12} />
                                        ~{Math.round(s.timeMinutes)} min
                                      </span>
                                    )}
                                    {s.difficulty && (
                                      <span className={styles.missionMetaTag}>
                                        <AlertTriangle size={12} />
                                        {s.difficulty.split(' | ').map(d => d.split(': ')[0]).join(' · ')}
                                      </span>
                                    )}
                                    {s.rep && (
                                      <span className={`${styles.missionMetaTag} ${s.rep !== 'Neutral' ? styles.missionMetaRep : ''}`}>
                                        <Star size={12} />
                                        {s.rep}
                                      </span>
                                    )}
                                    {s.systems && (
                                      <span className={`${styles.missionMetaTag} ${styles.missionMetaSystem}`}>
                                        <MapPin size={12} />
                                        {s.systems}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ));
                            })()}
                          </div>
                        ) : (
                          <div className={styles.plannerObtainDefault}>
                            <Gem size={14} />
                            <span>Default blueprint — available from start</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ─── Quantity & Craft Controls ─── */}
                    <div className={styles.plannerControlsRow}>
                      <div className={styles.plannerControlBlock}>
                        <label className={styles.plannerControlLabel}>Quantity</label>
                        <div className={styles.plannerQuantityRow}>
                          <button
                            onClick={() => updatePlannerEntry(entry.blueprintId, { quantity: Math.max(1, entry.quantity - 1) })}
                            disabled={entry.quantity <= 1}
                          >−</button>
                          <input
                            type="number"
                            min={1}
                            max={100}
                            value={entry.quantity}
                            onChange={e => updatePlannerEntry(entry.blueprintId, { quantity: Math.max(1, Math.min(100, parseInt(e.target.value) || 1)) })}
                          />
                          <button
                            onClick={() => updatePlannerEntry(entry.blueprintId, { quantity: Math.min(100, entry.quantity + 1) })}
                          >+</button>
                        </div>
                      </div>
                      <div className={styles.plannerControlBlock}>
                        <label className={styles.plannerControlLabel}>Total Craft Time</label>
                        <span className={styles.plannerCraftTimeValue}>
                          <Clock size={14} />
                          {formatCraftTime(entry.detail.craftTimeSeconds * entry.quantity)}
                        </span>
                      </div>
                    </div>

                    {/* ─── Required Materials ─── */}
                    <div className={styles.plannerSection}>
                      <div className={styles.plannerSectionHeader}>
                        <Package size={13} />
                        <span>Required Materials</span>
                      </div>
                      <div className={styles.plannerMaterialsGrid}>
                        {entry.detail.ingredients.map((ing, idx) => {
                          const miningIcon = ing.miningType === 'Ship Mining' ? <Pickaxe size={13} />
                            : ing.miningType === 'FPS Mining' ? <Crosshair size={13} />
                            : ing.miningType === 'Ground Mining' ? <Pickaxe size={13} />
                            : ing.miningType === 'Gatherable' ? <Leaf size={13} />
                            : <FlaskConical size={13} />;
                          return (
                            <div key={idx} className={`${styles.plannerMaterialCard} ${ing.rarity ? styles['materialBorder' + ing.rarity] : ''}`}>
                              <div className={styles.materialCardHeader}>
                                <div className={styles.materialCardIcon}>
                                  {miningIcon}
                                </div>
                                <div className={styles.materialCardTitle}>
                                  <span className={styles.materialCardName}>{ing.resourceDisplayName || ing.resourceName}</span>
                                  <div className={styles.materialCardBadges}>
                                    {ing.rarity && (
                                      <span className={`${styles.plannerRarityBadge} ${styles['rarity' + ing.rarity]}`}>{ing.rarity}</span>
                                    )}
                                    {ing.miningType && (
                                      <span className={styles.plannerMiningBadge}>{ing.miningType}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className={styles.materialCardDetails}>
                                <div className={styles.materialCardStat}>
                                  <span className={styles.materialStatLabel}>Amount</span>
                                  <span className={styles.materialStatValue}>{(ing.quantityScu * entry.quantity).toFixed(3)} SCU</span>
                                </div>
                                {ing.location && (
                                  <div className={styles.materialCardStat}>
                                    <span className={styles.materialStatLabel}>Location</span>
                                    <span className={styles.materialStatValueLocation}>
                                      <MapPin size={11} /> {ing.location}
                                    </span>
                                  </div>
                                )}
                                {ing.minQuality > 0 && (
                                  <div className={styles.materialCardStat}>
                                    <span className={styles.materialStatLabel}>Min Quality</span>
                                    <span className={styles.materialStatValue}>Q{ing.minQuality}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* ─── Quality Tuning ─── */}
                    {getSlotNames(entry.detail).length > 0 && (
                      <div className={styles.plannerSection}>
                        <div className={styles.plannerSectionHeader}>
                          <SlidersHorizontal size={13} />
                          <span>Quality Tuning</span>
                          <div className={styles.plannerSetAllRow}>
                            {[0, 500, 1000].map(q => (
                              <button key={q} className={styles.setAllButtonSmall} onClick={() => setPlannerAllSlots(entry.blueprintId, q)}>Q{q}</button>
                            ))}
                            <button className={`${styles.setAllButtonSmall} ${styles.resetButton}`} onClick={() => setPlannerAllSlots(entry.blueprintId, 500)} title="Reset to Q500">
                              <RotateCcw size={10} />
                            </button>
                          </div>
                        </div>
                        <div className={styles.plannerSectionBody}>
                          {getSlotNames(entry.detail).map(slotName => {
                            const slotQ = entry.slotQualities[slotName] ?? 500;
                            const slotMods = entry.detail.statModifiers.filter(m => m.slotName === slotName);
                            const ing = entry.detail.ingredients.find(i => i.slotName === slotName);
                            const materialLabel = ing ? (ing.resourceDisplayName || ing.resourceName) : slotName;
                            return (
                              <div key={slotName} className={styles.plannerSlotRow}>
                                <div className={styles.plannerSlotInfo}>
                                  <div className={styles.plannerSlotNameRow}>
                                    <span className={styles.plannerSlotName}>{materialLabel}</span>
                                    {ing?.rarity && (
                                      <span className={`${styles.plannerRarityBadge} ${styles['rarity' + ing.rarity]}`}>{ing.rarity}</span>
                                    )}
                                  </div>
                                  <span className={styles.plannerSlotQ}>Q{slotQ}</span>
                                </div>
                                <input
                                  type="range"
                                  min={0}
                                  max={1000}
                                  value={slotQ}
                                  onChange={e => updatePlannerSlotQuality(entry.blueprintId, slotName, parseInt(e.target.value))}
                                  className={styles.qualitySlider}
                                  style={{ flex: 1, minWidth: 0 }}
                                />
                                <div className={styles.plannerSlotStats}>
                                  {slotMods.map((mod, j) => {
                                    const val = interpolateModifier(mod, slotQ);
                                    const good = isGoodModifier(val, mod.statName);
                                    return (
                                      <span key={j} className={`${styles.plannerSlotStat} ${good ? styles.modifierPositive : val === 1 ? '' : styles.modifierNegative}`}>
                                        {cleanStatName(mod.statName)}: {val.toFixed(2)}×
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Summary Section */}
              <div className={styles.plannerSummary}>
                <h3 className={styles.plannerSummaryTitle}>
                  <Package size={18} />
                  Shopping List
                </h3>

                <div className={styles.plannerSummaryStats}>
                  <div className={styles.plannerSummaryStat}>
                    <span className={styles.plannerSummaryValue}>{plannerEntries.length}</span>
                    <span className={styles.plannerSummaryLabel}>Blueprint{plannerEntries.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className={styles.plannerSummaryStat}>
                    <span className={styles.plannerSummaryValue}>{plannerEntries.reduce((s, e) => s + e.quantity, 0)}</span>
                    <span className={styles.plannerSummaryLabel}>Total Items</span>
                  </div>
                  <div className={styles.plannerSummaryStat}>
                    <span className={styles.plannerSummaryValue}>{formatCraftTime(plannerTotalCraftTime)}</span>
                    <span className={styles.plannerSummaryLabel}>Total Craft Time</span>
                  </div>
                  <div className={styles.plannerSummaryStat}>
                    <span className={styles.plannerSummaryValue}>{plannerMaterialSummary.length}</span>
                    <span className={styles.plannerSummaryLabel}>Unique Materials</span>
                  </div>
                </div>

                {/* Material shopping cards */}
                <div className={styles.shoppingGrid}>
                  {plannerMaterialSummary.map((mat, idx) => {
                    const miningIcon = mat.miningType === 'Ship Mining' ? <Pickaxe size={16} />
                      : mat.miningType === 'FPS Mining' ? <Crosshair size={16} />
                      : mat.miningType === 'Ground Mining' ? <Pickaxe size={16} />
                      : mat.miningType === 'Gatherable' ? <Leaf size={16} />
                      : <FlaskConical size={16} />;
                    return (
                      <div key={idx} className={`${styles.shoppingCard} ${mat.rarity ? styles['materialBorder' + mat.rarity] : ''}`}>
                        <div className={styles.shoppingCardTop}>
                          <div className={styles.shoppingCardIcon}>
                            {miningIcon}
                          </div>
                          <div className={styles.shoppingCardName}>
                            <strong>{mat.displayName}</strong>
                            <div className={styles.shoppingCardBadges}>
                              {mat.rarity && (
                                <span className={`${styles.plannerRarityBadge} ${styles['rarity' + mat.rarity]}`}>{mat.rarity}</span>
                              )}
                              {mat.miningType && (
                                <span className={styles.plannerMiningBadge}>{mat.miningType}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={styles.shoppingCardStats}>
                          <div className={styles.shoppingStatRow}>
                            <span className={styles.shoppingStatLabel}>Total Needed</span>
                            <span className={styles.shoppingStatHighlight}>{mat.totalScu.toFixed(3)} SCU</span>
                          </div>
                          <div className={styles.shoppingStatRow}>
                            <span className={styles.shoppingStatLabel}>Target Quality</span>
                            <span className={styles.shoppingStatQuality}>&ge; Q{mat.targetQuality}</span>
                          </div>
                          {mat.location && (
                            <div className={styles.shoppingStatRow}>
                              <span className={styles.shoppingStatLabel}>Location</span>
                              <span className={styles.shoppingStatLocation}><MapPin size={11} /> {mat.location}</span>
                            </div>
                          )}
                          <div className={styles.shoppingStatRow}>
                            <span className={styles.shoppingStatLabel}>Used By</span>
                            <span className={styles.shoppingStatDim}>{mat.blueprintCount} blueprint{mat.blueprintCount !== 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Quality effects summary */}
                {plannerQualityEffects.some(e => e.stats.length > 0) && (
                  <div className={styles.plannerSummarySection}>
                    <div className={styles.plannerSummarySectionHeader}>
                      <SlidersHorizontal size={14} />
                      <span>Combined Quality Effects</span>
                    </div>
                    <div className={styles.plannerSummarySectionBody}>
                      {plannerQualityEffects.filter(e => e.stats.length > 0).map((entry, ei) => (
                        <div key={ei} className={styles.qualityEffectGroup}>
                          <div className={styles.qualityEffectLabel}>{entry.entryName}</div>
                          <div className={styles.modifierGrid}>
                            {entry.stats.map((stat, idx) => {
                              const isGood = isGoodModifier(stat.combined, stat.statName);
                              const pctChange = ((stat.combined - 1) * 100);
                              return (
                                <div key={idx} className={`${styles.modifierCard} ${isGood ? styles.modifierCardGood : stat.combined === 1 ? '' : styles.modifierCardBad}`}>
                                  <div className={styles.modifierStat}>{cleanStatName(stat.statName)}</div>
                                  <div className={styles.modifierCurrentValue}>
                                    <span className={isGood ? styles.modifierPositive : stat.combined === 1 ? '' : styles.modifierNegative}>
                                      {stat.combined.toFixed(3)}×
                                    </span>
                                    <span className={`${styles.modifierPctBadge} ${isGood ? styles.modifierPctGood : stat.combined === 1 ? styles.modifierPctNeutral : styles.modifierPctBad}`}>
                                      {pctChange >= 0 ? '+' : ''}{pctChange.toFixed(1)}%
                                    </span>
                                  </div>
                                  {stat.perSlot.length > 1 && (
                                    <div className={styles.combinedBreakdown}>
                                      {stat.perSlot.map((ps, j) => (
                                        <span key={j} className={styles.breakdownChip}>
                                          {slotMaterialLabel(selectedBlueprint!, ps.slotName)}: {ps.modifier.toFixed(2)}×
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Clear button */}
                <div className={styles.plannerClearRow}>
                  <button
                    className={styles.clearButton}
                    onClick={() => setPlannerEntries([])}
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </>
          )}
          </>
          )}
        </>
      )}

      {/* ===== DETAIL MODAL ===== */}
      {selectedBlueprint && (
        <div className={styles.modalOverlay} onClick={() => setSelectedBlueprint(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            {selectedBlueprint.imageUrl && (
              <div className={styles.modalHeroBanner}>
                <img src={selectedBlueprint.imageUrl} alt="" className={styles.modalHeroImage} title="Image may not be exact to variant" />
              </div>
            )}
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                {!selectedBlueprint.imageUrl && (
                  <div className={styles.modalIcon} style={{ background: getCategoryIcon(selectedBlueprint.category, selectedBlueprint.subCategory).gradient }}>
                    {getCategoryIcon(selectedBlueprint.category, selectedBlueprint.subCategory).icon}
                  </div>
                )}
                <div>
                  <div className={styles.modalTitle}>
                    {selectedBlueprint.outputItemName || selectedBlueprint.displayName || selectedBlueprint.recordName}
                  </div>
                  <div className={styles.modalSubtitle}>
                    {getCategoryLabel(selectedBlueprint.category, selectedBlueprint.subCategory)}
                    {selectedBlueprint.outputItemName && selectedBlueprint.outputItemName !== selectedBlueprint.recordName && (
                      <> &bull; Internal: {selectedBlueprint.recordName}</>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.modalHeaderActions}>
                <button
                  className={`${styles.addToPlannerBtn} ${plannerEntries.some(e => e.blueprintId === selectedBlueprint.id) ? styles.addToPlannerBtnAdded : ''}`}
                  onClick={() => addToPlannerFromDetail(selectedBlueprint)}
                  disabled={plannerEntries.some(e => e.blueprintId === selectedBlueprint.id)}
                >
                  <Plus size={14} />
                  {plannerEntries.some(e => e.blueprintId === selectedBlueprint.id) ? 'In Planner' : 'Add to Planner'}
                </button>
                <button className={styles.closeButton} onClick={() => setSelectedBlueprint(null)}>
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className={styles.modalBody}>
              {/* Overview Stats Row */}
              <div className={styles.modalStatsRow}>
                <div className={styles.modalStatBox}>
                  <Clock size={14} />
                  <div>
                    <div className={styles.modalStatLabel}>Craft Time</div>
                    <div className={styles.modalStatValue}>{formatCraftTime(selectedBlueprint.craftTimeSeconds)}</div>
                  </div>
                </div>
                {selectedBlueprint.dismantleTimeSeconds != null && selectedBlueprint.dismantleTimeSeconds > 0 && (
                  <div className={styles.modalStatBox}>
                    <Hammer size={14} />
                    <div>
                      <div className={styles.modalStatLabel}>Dismantle Time</div>
                      <div className={styles.modalStatValue}>{formatCraftTime(selectedBlueprint.dismantleTimeSeconds)}</div>
                    </div>
                  </div>
                )}
                {selectedBlueprint.dismantleEfficiency != null && selectedBlueprint.dismantleEfficiency > 0 && (
                  <div className={styles.modalStatBox}>
                    <Package size={14} />
                    <div>
                      <div className={styles.modalStatLabel}>Dismantle Recovery</div>
                      <div className={styles.modalStatValue}>{(selectedBlueprint.dismantleEfficiency * 100).toFixed(0)}%</div>
                    </div>
                  </div>
                )}
                <div className={styles.modalStatBox}>
                  <FlaskConical size={14} />
                  <div>
                    <div className={styles.modalStatLabel}>Materials</div>
                    <div className={styles.modalStatValue}>{selectedBlueprint.ingredients.length} slot{selectedBlueprint.ingredients.length !== 1 ? 's' : ''}</div>
                  </div>
                </div>
              </div>

              {/* Recipe / Ingredients */}
              {selectedBlueprint.ingredients.length > 0 && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <FlaskConical size={16} />
                    Recipe
                    <span className={styles.sectionHint}>Materials needed to craft this item</span>
                  </h3>
                  <table className={styles.recipeTable}>
                    <thead>
                      <tr>
                        <th>Slot</th>
                        <th>Material</th>
                        <th>Quantity (SCU)</th>
                        <th>Min Quality</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBlueprint.ingredients.map((ing, idx) => (
                        <tr key={idx}>
                          <td>{ing.slotDisplayName || ing.slotName}</td>
                          <td>{ing.resourceDisplayName || ing.resourceName}</td>
                          <td>{ing.quantityScu.toFixed(3)}</td>
                          <td>{ing.minQuality > 0 ? `Q${ing.minQuality}` : 'Any'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Quality Effects — Per-Slot Quality Controls
                  Render sliders whenever there are ingredient slots (not just when there are
                  stat modifiers). Items like magazines have ingredients but no stat-modifying
                  slots — sliders still let the player choose material quality. */}
              {getSlotNames(selectedBlueprint).length > 0 && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <SlidersHorizontal size={16} />
                    {selectedBlueprint.statModifiers.length > 0 ? 'Quality Effects' : 'Material Quality'}
                  </h3>

                  {/* Info box */}
                  <div className={styles.qualityInfoBox}>
                    <Info size={14} />
                    {selectedBlueprint.statModifiers.length > 0 ? (
                      <p>
                        Each ingredient slot independently affects different stats based on material quality (0–1000).
                        Modifiers are <strong>multipliers</strong> on the item's base stats — <strong>1.0× = no change</strong>.
                        When multiple slots affect the same stat, their modifiers <strong>multiply together</strong>.
                        For recoil/spread stats, <em>lower</em> is better.
                      </p>
                    ) : (
                      <p>
                        Choose the material quality (0–1000) for each ingredient slot. This blueprint does not have stat-modifying slots, so quality affects yield/cost only.
                      </p>
                    )}
                  </div>

                  {/* Set All Quality shortcut */}
                  <div className={styles.setAllQualityRow}>
                    <label className={styles.setAllLabel}>Set All Slots:</label>
                    {[0, 250, 500, 750, 1000].map(q => (
                      <button
                        key={q}
                        className={styles.setAllButton}
                        onClick={() => {
                          const sq: Record<string, number> = {};
                          for (const slot of getSlotNames(selectedBlueprint)) sq[slot] = q;
                          setSlotQualities(sq);
                        }}
                      >
                        Q{q}
                      </button>
                    ))}
                    <button
                      className={`${styles.setAllButton} ${styles.resetButton}`}
                      onClick={() => {
                        if (selectedBlueprint) setSlotQualities(defaultSlotQualities(selectedBlueprint));
                      }}
                      title="Reset all slots to default (Q500)"
                    >
                      <RotateCcw size={12} /> Reset
                    </button>
                  </div>

                  {/* Per-Slot Quality Sliders with affected stats */}
                  {(() => {
                    const slotNames = getSlotNames(selectedBlueprint);
                    // Find ingredient info per slot
                    const ingBySlot: Record<string, CraftingIngredient | undefined> = {};
                    for (const ing of selectedBlueprint.ingredients) {
                      ingBySlot[ing.slotName] = ing;
                    }
                    // Group modifiers by slot
                    const modsBySlot: Record<string, CraftingStatModifier[]> = {};
                    for (const mod of selectedBlueprint.statModifiers) {
                      if (!modsBySlot[mod.slotName]) modsBySlot[mod.slotName] = [];
                      modsBySlot[mod.slotName].push(mod);
                    }
                    return slotNames.map(slotName => {
                      const slotQ = slotQualities[slotName] ?? 500;
                      const ing = ingBySlot[slotName];
                      const mods = modsBySlot[slotName] || [];
                      return (
                        <div key={slotName} className={styles.slotQualitySection}>
                          <div className={styles.slotQualityHeader}>
                            <div className={styles.slotQualityTitle}>
                              <span className={styles.slotQualityName}>
                                {ing ? (ing.resourceDisplayName || ing.resourceName) : slotName}
                              </span>
                              <span className={styles.slotQualityMaterial}>
                                {ing
                                  ? `${slotName} slot — ${ing.quantityScu.toFixed(3)} SCU`
                                  : 'Item slot (no raw material)'}
                              </span>
                            </div>
                            <div className={styles.qualityValue}>
                              <span className={styles.qualityNumber}>{slotQ}</span>
                              <span className={styles.qualityMax}>/ 1000</span>
                            </div>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={1000}
                            step={1}
                            value={slotQ}
                            onChange={e => setSlotQualities(prev => ({ ...prev, [slotName]: parseInt(e.target.value) }))}
                            className={styles.qualitySlider}
                          />
                          <div className={styles.qualityMarkers}>
                            <span onClick={() => setSlotQualities(prev => ({ ...prev, [slotName]: 0 }))}>0</span>
                            <span onClick={() => setSlotQualities(prev => ({ ...prev, [slotName]: 250 }))}>250</span>
                            <span onClick={() => setSlotQualities(prev => ({ ...prev, [slotName]: 500 }))}>500</span>
                            <span onClick={() => setSlotQualities(prev => ({ ...prev, [slotName]: 750 }))}>750</span>
                            <span onClick={() => setSlotQualities(prev => ({ ...prev, [slotName]: 1000 }))}>1000</span>
                          </div>
                          {/* Stats affected by this slot */}
                          <div className={styles.slotModifierList}>
                            {mods.map((mod, idx) => {
                              const currentMod = interpolateModifier(mod, slotQ);
                              const isGood = isGoodModifier(currentMod, mod.statName);
                              const cleanName = cleanStatName(mod.statName);
                              const tooltip = getStatTooltip(mod.statName);
                              const inverted = isInvertedStat(mod.statName);
                              const pctChange = ((currentMod - 1) * 100);
                              return (
                                <div key={idx} className={`${styles.slotModifierRow} ${isGood ? styles.modifierCardGood : currentMod === 1 ? '' : styles.modifierCardBad}`}>
                                  <div className={styles.slotModifierInfo}>
                                    <span className={styles.slotModifierStatName} title={tooltip}>
                                      {cleanName}
                                      {inverted && <span className={styles.invertedBadge}>↓ better</span>}
                                    </span>
                                    <span className={styles.slotModifierRange}>
                                      {formatModifier(mod.modifierAtStart)} → {formatModifier(mod.modifierAtEnd)}
                                    </span>
                                  </div>
                                  <div className={styles.slotModifierResult}>
                                    <span className={isGood ? styles.modifierPositive : currentMod === 1 ? '' : styles.modifierNegative}>
                                      {currentMod.toFixed(3)}×
                                    </span>
                                    <span className={`${styles.modifierPctBadge} ${isGood ? styles.modifierPctGood : currentMod === 1 ? styles.modifierPctNeutral : styles.modifierPctBad}`}>
                                      {pctChange >= 0 ? '+' : ''}{pctChange.toFixed(1)}%
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}

                  {/* Combined Stats Summary */}
                  {(() => {
                    const combined = computeCombinedStats(selectedBlueprint, slotQualities);
                    // Only show combined section if any stat has >1 contributing slot
                    const hasMultiSlot = combined.some(s => s.perSlot.length > 1);
                    if (!hasMultiSlot && combined.length === 0) return null;
                    return (
                      <div className={styles.combinedStatsSection}>
                        <h4 className={styles.combinedStatsTitle}>
                          <Layers size={14} />
                          Combined Result
                          <span className={styles.sectionHint}>All slot modifiers multiplied together</span>
                        </h4>
                        <div className={styles.modifierGrid}>
                          {combined.map((stat, idx) => {
                            const isGood = isGoodModifier(stat.combined, stat.statName);
                            const pctChange = ((stat.combined - 1) * 100);
                            const cleanName = cleanStatName(stat.statName);
                            const inverted = isInvertedStat(stat.statName);
                            return (
                              <div key={idx} className={`${styles.modifierCard} ${isGood ? styles.modifierCardGood : stat.combined === 1 ? '' : styles.modifierCardBad}`}>
                                <div className={styles.modifierHeader}>
                                  <div className={styles.modifierStat} title={getStatTooltip(stat.statName)}>
                                    {cleanName}
                                    <HelpCircle size={12} className={styles.helpIcon} />
                                  </div>
                                  {inverted && <span className={styles.invertedBadge}>Lower = Better</span>}
                                </div>
                                <div className={styles.modifierCurrentValue}>
                                  <span className={isGood ? styles.modifierPositive : stat.combined === 1 ? '' : styles.modifierNegative}>
                                    {stat.combined.toFixed(3)}×
                                  </span>
                                  <span className={`${styles.modifierPctBadge} ${isGood ? styles.modifierPctGood : stat.combined === 1 ? styles.modifierPctNeutral : styles.modifierPctBad}`}>
                                    {pctChange >= 0 ? '+' : ''}{pctChange.toFixed(1)}%
                                  </span>
                                </div>
                                {stat.perSlot.length > 1 && (
                                  <div className={styles.combinedBreakdown}>
                                    {stat.perSlot.map((ps, j) => (
                                      <span key={j} className={styles.breakdownChip}>
                                        {slotMaterialLabel(selectedBlueprint!, ps.slotName)}: {ps.modifier.toFixed(3)}×
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* How to Obtain */}
              {selectedBlueprint.availableFrom.length > 0 && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <Map size={16} />
                    How to Obtain
                    <span className={styles.sectionHint}>Complete missions to earn this blueprint</span>
                  </h3>
                  <div className={styles.sourcesList}>
                    {(() => {
                      // Flatten all contractor sources across all pools, deduplicating
                      const allSources: { contractorName: string; missionName: string; poolFaction: string; poolMission: string }[] = [];
                      const seen = new Set<string>();
                      selectedBlueprint.availableFrom.forEach(src => {
                        if (src.contractors && src.contractors.length > 0) {
                          src.contractors.forEach(c => {
                            const key = `${c.contractorName}|${c.missionName}`;
                            if (!seen.has(key)) {
                              seen.add(key);
                              allSources.push({ contractorName: c.contractorName, missionName: c.missionName, poolFaction: src.faction, poolMission: src.missionType });
                            }
                          });
                        } else {
                          const key = `${src.faction}|${src.missionType}`;
                          if (!seen.has(key)) {
                            seen.add(key);
                            allSources.push({ contractorName: src.faction, missionName: src.missionType, poolFaction: src.faction, poolMission: src.missionType });
                          }
                        }
                      });
                      return allSources.map((source, idx) => (
                        <div key={idx} className={styles.sourceCard}>
                          <div className={styles.sourceIcon}>
                            <Shield size={18} />
                          </div>
                          <div className={styles.sourceInfo}>
                            <div className={styles.sourceFaction}>{source.contractorName}</div>
                            <div className={styles.sourceMission}>
                              Mission: <strong>{source.missionName}</strong>
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {selectedBlueprint.availableFrom.length === 0 && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <Map size={16} />
                    How to Obtain
                  </h3>
                  <div className={styles.defaultBlueprintNote}>
                    <Gem size={16} />
                    <p>This is a <strong>default blueprint</strong> — available to all players from the start. No mission reward required.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail loading overlay */}
      {detailLoading && (
        <div className={styles.modalOverlay}>
          <div className={styles.loading} style={{ minHeight: 'unset' }}>
            <div className={styles.spinner}></div>
            <span style={{ color: '#fff' }}>Loading blueprint details...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CraftingBlueprints;
